import * as React from 'react';
import { mapSemestersToModel } from '../../Utils/StudyModelUtils';
import type { LegacyStudyModelSectionViewModel } from '~/Pages/CampusStudyPage/ViewModel/StudyModel/LegacyStudyModelSectionViewModel.csharp';
import type { LegacyStudyModelViewModel } from '~/Pages/CampusStudyPage/ViewModel/StudyModel/LegacyStudyModelViewModel.csharp';
import type { LegacySubjectViewModel } from '~/Pages/CampusStudyPage/ViewModel/Subject/LegacySubjectViewModel.csharp';
import { AccordionList } from '~/Partials/AccordionComponent/AccordionList';
import { ActivityIndicator } from '~/Partials/ActivityIndicator/ActivityIndicator';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { useFetchData } from '~/Partials/FetchData/useFetchData';
import { Link } from '~/Partials/Link/Link';
import { LinkVariant } from '~/Partials/Link/LinkVariant.csharp';
import { StudySubjects } from '~/Partials/StudySubjects/StudySubjects';
import styles from './StudyModel.module.css';

export const StudyModel: React.FC<React.PropsWithChildren<LegacyStudyModelSectionViewModel>> = props => {
    const { attributes, settings } = props;

    const {
        loading,
        error,
        data: studyModel,
    } = useFetchData<LegacyStudyModelViewModel>({
        url: attributes.getStudyModelUrl,
        shouldMakeRequest: true,
    });

    const parseStudyModelToAccordion = () => ({
        headerContent: attributes.displayName,
        children: (
            <>
                <div className="SemesterList__infoHeader">
                    {settings.disableStudyModel ? (
                        <HtmlDiv html={settings.disabledStudyModelMessage} />
                    ) : (
                        attributes.subjectToChanges
                    )}
                </div>
                {!settings.disableStudyModel && (
                    <>
                        {loading && (
                            <span>
                                <ActivityIndicator modal={false} />
                            </span>
                        )}
                        {!!studyModel && (
                            <div className="SemesterList">
                                {!!studyModel.prerequisites &&
                                    renderPrerequisites(
                                        studyModel.prerequisites.subjects,
                                        attributes.prerequisitesDisplayName
                                    )}
                                <StudySubjects programSemesters={mapSemestersToModel(studyModel.semesters)} />
                            </div>
                        )}
                        {!!error && <span className="SemesterList__error">{error}</span>}
                    </>
                )}
            </>
        ),
    });

    const renderPrerequisites = (subjects: LegacySubjectViewModel[], displayName: string) => {
        if (subjects.length > 0) {
            return (
                <ul className="SemesterList__content SemesterList__content--noCounter">
                    <li>
                        <h4>{displayName}</h4>
                        <ul className="SemesterList__subContent">
                            {subjects.map((subject: LegacySubjectViewModel, idx: number) => (
                                <li key={idx}>
                                    {subject.url ? (
                                        <Link
                                            variant={LinkVariant.LightBlack}
                                            href={subject.url}
                                            label={subject.name}
                                        />
                                    ) : (
                                        <span>{subject.name}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </li>
                </ul>
            );
        }
    };

    return (
        <div className={styles.StudyModel}>
            <AccordionList accordions={[parseStudyModelToAccordion()]} />
        </div>
    );
};
